import { z } from "zod";
import {
  formConfigToZodSchemas,
  getFormConfigDefaultValues,
} from "../form-config-to-zod-schema";
import {
  getFormKeys,
  type FormConfig,
  type FormConfigInPages,
  type I18NFunction,
  type SectionConfig,
} from "../form-config-types";
import {
  mapToNumPatientsEnumLabels,
  mapToStandardPercentageEnumLabels,
  mapToTypesOfSpecialistsEnumLabels,
  mapToTypesOfSpecialistsInvolvedInTheMDTEnumLabels,
  mapToYesNoEnumLabels,
  numPatientsEnum,
  standardPercentageEnum,
  typesOfSpecialistsEnum,
  typesOfSpecialistsInvolvedInTheMDTEnum,
  yesNoEnum,
} from "./form-data-enums";
import { arrayErrorMap, enumErrorMap } from "./form-data-utils";

const treatmentEarlyStageFormConfigInPages = (l: I18NFunction) =>
  [
    // page 1
    {
      pageTitle: "",
      pageDescription: "",
      formFields: {
        mtdDiscussion: {
          type: "object",
          label: "",
          properties: {
            // 3.1 When does a MDT discussion take place for a patient considered at high risk of recurrence? [open box]
            whenDoesMDTDiscussionTakePlace: {
              type: "textarea",
              label: l(
                "1. When does an MDT discussion take place for a CSCC patient considered at high-risk of recurrence?",
                "1. Quand a lieu une discussion de l'EMD pour un patient atteint de CSCC qui présente un risque élevé de récidive?"
              ),
              schema: z
                .string({
                  required_error: l(
                    "Please enter a value",
                    "Ce champ est requis"
                  ),
                })
                .min(1, {
                  message: l("Please enter a value", "Ce champ est requis"),
                }),
            },

            // 3.2 Who is typically involved in this discussion?
            // PCP/GPs [checkbox]
            // Dermatologists [checkbox]
            // ENTs [checkbox]
            // Surgeons [checkbox]
            // Radio oncologists [checkbox]
            // Pathologists [checkbox]
            // Medical oncologists [checkbox]
            // Others (please specify) [open box]
            whoIsTypicallyInvolvedInThisDiscussion: {
              type: "checkboxGroup",
              label: l(
                "2. Who is typically involved in this discussion?",
                "2. Qui est généralement impliqué dans cette discussion?"
              ),
              schema: z.array(z.enum(typesOfSpecialistsInvolvedInTheMDTEnum), {
                errorMap: arrayErrorMap(l),
              }),
              mapToLabels: mapToTypesOfSpecialistsInvolvedInTheMDTEnumLabels,
            },
            whoIsTypicallyInvolvedInThisDiscussionOther: {
              type: "textarea",
              label: l(
                "Others (please specify)",
                "Qui est généralement impliqué dans cette discussion?"
              ),
              schema: z
                .string({
                  required_error: l(
                    "Please enter a value",
                    "Ce champ est requis"
                  ),
                })
                .nullish(),
              dependencies: [
                {
                  if: [
                    "mtdDiscussion.whoIsTypicallyInvolvedInThisDiscussion",
                    "not-contains-case-insensitive",
                    "Others",
                  ],
                  then: "hide",
                },
                {
                  if: [
                    "mtdDiscussion.whoIsTypicallyInvolvedInThisDiscussion",
                    "contains-case-insensitive",
                    "Others",
                  ],
                  then: "require",
                },
              ],
            },
          },
        },
      },
    },
    // page
    {
      pageTitle: "",
      pageDescription: "",
      formFields: {
        // 3.4 What is your current involvement and management approach following surgical resection of high risk CSCC? [open box] [Not applicable; If selected, go to question 3.5]
        // Is it the same for BCC? [Yes or No]
        // [If selected No] Please explain the differences [open box]
        involvementAndManagementApproachFollowingSurgicalResectionOfHighRiskCSCC:
          {
            type: "object",
            label: "",
            properties: {
              whatIsYourCurrentInvolvementAndManagementApproachFollowingSurgicalResectionOfHighRiskCSCC:
                {
                  type: "textarea",
                  label: l(
                    "3. What is your current involvement and management approach following surgical resection of high-risk CSCC?",
                    "3. Quelle est votre participation actuelle et quelle est votre approche de prise en charge à la suite d'une résection chirurgicale d'un CSCC à risque élevé?"
                  ),
                  schema: z
                    .string({
                      required_error: l(
                        "Please enter a value",
                        "Ce champ est requis"
                      ),
                      invalid_type_error: l(
                        "Please enter a value",
                        "Ce champ est requis"
                      ),
                    })
                    .or(z.literal("not_applicable")),
                },
            },
          },

        radiotherapyInHighRiskCSCC: {
          type: "object",
          label: l(
            "4. What percentage of high-risk CSCC patients receive radiotherapy following surgical resection?",
            "4. Quel pourcentage de patients atteints de CSCC à risque élevé reçoivent de la radiothérapie après une résection chirurgicale?"
          ),
          offerNotApplicableForEntireObject: true,
          properties: {
            // 3.5 What percentage of high-risk CSCC patients receive radiotherapy following surgical resection? [box with selection of 1-100] [Not applicable; If selected, go to question 3.7]
            whatPercentageOfHighRiskCSCCPatientsReceiveRadiotherapyFollowingSurgicalResection:
              {
                type: "radioGroup",
                label: "",
                schema: z
                  .enum(standardPercentageEnum, {
                    errorMap: enumErrorMap(l),
                  })
                  .or(z.literal("not_applicable")),
                mapToLabels: mapToStandardPercentageEnumLabels,
                hideNotApplicableCheckbox: true,
              },

            // 3.6 What are the specific considerations for radiotherapy post-surgical resection of high risk CSCC, as standard of care? [open box] [Not applicable]
            whatAreTheSpecificConsiderationsForRadiotherapyPostSurgicalResectionOfHighRiskCSCCAsStandardOfCare:
              {
                type: "textarea",
                label: l(
                  "5. What are the specific considerations for radiotherapy post-surgical resection of high-risk CSCC, as standard of care?",
                  "5. Quelles sont les considérations spécifiques pour la radiothérapie suivant une résection chirurgicale de CSCC à risque élevé, comme standard de soins?"
                ),
                schema: z
                  .string({
                    required_error: l(
                      "Please enter a value",
                      "Ce champ est requis"
                    ),
                    invalid_type_error: l(
                      "Please enter a value",
                      "Ce champ est requis"
                    ),
                  })
                  .or(z.literal("not_applicable")),
              },

            // 3.7 What are some reasons why a patient might not be considered suitable for radiotherapy? [open box] [Not applicable; If selected, go to question 3.8]
            // Is it the same for BCC? [Yes or No]
            // [If selected No] Please explain the differences [open box]
            whatAreSomeReasonsWhyAPatientMightNotBeConsideredSuitableForRadiotherapy:
              {
                type: "textarea",
                label: l(
                  "6. What are some reasons why a patient might not be considered suitable for radiotherapy?",
                  "6. Quelles sont les raisons pour lesquelles un patient pourrait ne pas être considéré comme candidat à la radiothérapie?"
                ),
                schema: z
                  .string({
                    required_error: l(
                      "Please enter a value",
                      "Ce champ est requis"
                    ),
                    invalid_type_error: l(
                      "Please enter a value",
                      "Ce champ est requis"
                    ),
                  })
                  .or(z.literal("not_applicable")),
              },
          },
        },
      },
    },
    // page
    {
      pageTitle: "",
      pageDescription: "",
      formFields: {
        // 3.8 How are patients monitored post-surgery for recurrence? [open box] [Not applicable; If selected, go to question 3.9]
        // Is this consistent across your region? [Yes or No]
        // [If selected No] Please explain the disparities [open box]
        howArePatientsMonitoredPostSurgeryForRecurrence: {
          type: "object",
          label: l(
            "7. How are patients monitored post-surgery for recurrence?",
            "7. Comment les patients sont-ils surveillés après la chirurgie afin de déceler toute récidive?"
          ),
          offerNotApplicableForEntireObject: true,
          properties: {
            howArePatientsMonitoredPostSurgeryForRecurrence: {
              type: "textarea",
              label: "",
              schema: z
                .string({
                  required_error: l(
                    "Please enter a value",
                    "Ce champ est requis"
                  ),
                  invalid_type_error: l(
                    "Please enter a value",
                    "Ce champ est requis"
                  ),
                })
                .or(z.literal("not_applicable")),
              hideNotApplicableCheckbox: true,
            },
            isThisConsistentAcrossYourRegion: {
              type: "radioGroup",
              label: l(
                "Is this consistent across your region?",
                "Est-ce uniforme dans votre province?"
              ),
              schema: z
                .enum(yesNoEnum, {
                  errorMap: enumErrorMap(l),
                })
                .or(z.literal("not_applicable")),
              mapToLabels: mapToYesNoEnumLabels,
              hideNotApplicableCheckbox: true,
              cols: 2,
            },
            pleaseExplainTheDisparities: {
              type: "textarea",
              label: l(
                "Please explain the disparities",
                "Veuillez expliquer les disparités"
              ),
              schema: z
                .string({
                  required_error: l(
                    "Please enter a value",
                    "Ce champ est requis"
                  ),
                  invalid_type_error: l(
                    "Please enter a value",
                    "Ce champ est requis"
                  ),
                })
                .or(z.literal("not_applicable")),
              hideNotApplicableCheckbox: true,
              dependencies: [
                {
                  if: [
                    "howArePatientsMonitoredPostSurgeryForRecurrence.isThisConsistentAcrossYourRegion",
                    "not-contains-case-insensitive",
                    "No",
                  ],
                  then: "hide",
                },
                {
                  if: [
                    "howArePatientsMonitoredPostSurgeryForRecurrence.isThisConsistentAcrossYourRegion",
                    "contains-case-insensitive",
                    "No",
                  ],
                  then: "require",
                },
              ],
            },
          },
        },
      },
    },
    // page
    {
      pageTitle: "",
      pageDescription: "",
      formFields: {
        // 3.9 Is the adoption of treatment guidelines and practice for management of high risk CSCC consistent across your region? [Yes or No] [Not applicable; If selected, go to section 4]
        // [If selected No] Please explain the disparities. [open box]
        isTheAdoptionOfTreatmentGuidelinesAndPracticeForManagementOfHighRiskCSCCConsistentAcrossYourRegion:
          {
            type: "object",
            label: l(
              "8. Is the adoption of treatment guidelines and practice for management of high-risk CSCC consistent across your region?",
              "8. L'adoption des lignes directrices et de la pratique thérapeutique pour la prise en charge des CSCC à risque élevé est-elle uniforme dans votre province?"
            ),
            offerNotApplicableForEntireObject: true,
            properties: {
              yesOrNo: {
                type: "radioGroup",
                label: "",
                schema: z
                  .enum(yesNoEnum, {
                    errorMap: enumErrorMap(l),
                  })
                  .or(z.literal("not_applicable")),
                mapToLabels: mapToYesNoEnumLabels,
                hideNotApplicableCheckbox: true,
                cols: 2,
              },
              pleaseExplainTheDisparities: {
                type: "textarea",
                label: l(
                  "Please explain the disparities",
                  "Veuillez expliquer les disparités"
                ),
                schema: z
                  .string({
                    required_error: l(
                      "Please enter a value",
                      "Ce champ est requis"
                    ),
                    invalid_type_error: l(
                      "Please enter a value",
                      "Ce champ est requis"
                    ),
                  })
                  .nullish(),
                hideNotApplicableCheckbox: true,
                dependencies: [
                  {
                    if: [
                      "isTheAdoptionOfTreatmentGuidelinesAndPracticeForManagementOfHighRiskCSCCConsistentAcrossYourRegion.yesOrNo",
                      "not-contains-case-insensitive",
                      "No",
                    ],
                    then: "hide",
                  },
                  {
                    if: [
                      "isTheAdoptionOfTreatmentGuidelinesAndPracticeForManagementOfHighRiskCSCCConsistentAcrossYourRegion.yesOrNo",
                      "contains-case-insensitive",
                      "No",
                    ],
                    then: "require",
                  },
                ],
              },
            },
          },
        areThereEducationalGapsAcrossDifferentSpecialties: {
          type: "textarea",
          label: l(
            "9. From your perspective, are there any educational gaps across different HCP specialties regarding the management of high-risk CSCC?",
            "9. Selon vous, existe-t-il des lacunes éducatives au sein des différentes spécialités concernant la prise en charge des patients atteints de CSCC à risque élevé?"
          ),
          schema: z
            .string({
              required_error: l("Please enter a value", "Ce champ est requis"),
              invalid_type_error: l(
                "Please enter a value",
                "Ce champ est requis"
              ),
            })
            .min(1, {
              message: l("Please enter a value", "Ce champ est requis"),
            }),
        },
      },
    },
    {
      pageTitle: "",
      pageDescription: "",
      formFields: {
        earlyStateDoYouHaveAnyAdditionalComments: {
          type: "textarea",
          label: l(
            "Do you have any additional comments, suggestions, or concerns regarding the current version of the schema, particularly in relation to your own personal practice?",
            "Avez-vous d'autres commentaires, suggestions ou préoccupations au sujet de la version actuelle du schéma, particulièrement en ce qui concerne votre pratique personnelle?"
          ),
          schema: z
            .string({
              required_error: l("Please enter a value", "Ce champ est requis"),
              invalid_type_error: l(
                "Please enter a value",
                "Ce champ est requis"
              ),
            })
            .nullish(),
        },
      },
    },
  ] as const satisfies FormConfigInPages;

export const treatmentEarlyStageForm: (l: I18NFunction) => SectionConfig = (
  l
) => ({
  sectionId: "treatment-early-stage",
  sectionNumber: 3,
  title: l("Treatment - Early Stage", "Traitement - Stade précoce"),
  description: "",
  diagrams: [
    {
      src: l(
        "/diagrams/treatment_early_cscc.png",
        "/diagrams/treatment_early_cscc_fr.png"
      ),
      alt: l(
        "Treatment - Early Stage (CSCC)",
        "Traitement - Stade précoce (CSCC)"
      ),
    },
    {
      src: l(
        "/diagrams/treatment_early_bcc.png",
        "/diagrams/treatment_early_bcc_fr.png"
      ),
      alt: l(
        "Treatment - Early Stage (BCC)",
        "Traitement - Stade précoce (BCC)"
      ),
    },
  ],
  formPages: treatmentEarlyStageFormConfigInPages(l).map((page) => ({
    formConfig: page.formFields,
    formSchemas: formConfigToZodSchemas(page.formFields, l),
    defaultValues: getFormConfigDefaultValues(page.formFields),
  })),
  // formRootKeys: Object.keys(
  //   Object.assign({}, ...treatmentEarlyStageFormConfigInPages.flatMap((page) => page.formFields))
  // ),
  responseKeyToFormConfig: Object.fromEntries(
    treatmentEarlyStageFormConfigInPages(l).flatMap((page) =>
      Object.entries(page.formFields).map(([key, field]) => [key, field])
    )
  ),
});
